import { useState } from "react";
import axios from "axios";
import { Lock, BarChart3, Users, MapPin, Timer } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const EVENT_LABELS = {
  visitor_location: "Location checks",
  industry_selected: "Industry selected",
  process_selected: "Process viewed",
  calculator_started: "Calculator started",
  calculator_completed: "Calculator completed",
  lead_form_started: "Lead form started",
  lead_submitted: "Lead submitted",
  cta_clicked: "CTA clicked",
};

export default function AdminPage() {
  const [key, setKey] = useState(() => sessionStorage.getItem("od-admin-key") || "");
  const [input, setInput] = useState("");
  const [data, setData] = useState(null);
  const [leads, setLeads] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [error, setError] = useState("");

  const load = async (k) => {
    try {
      const headers = { "X-Admin-Key": k };
      const [summary, leadList, funnelRes] = await Promise.all([
        axios.get(`${API}/api/admin/miami/summary`, { headers }),
        axios.get(`${API}/api/admin/miami/leads`, { headers }),
        axios.get(`${API}/api/admin/funnel/summary`, { headers }),
      ]);
      setData(summary.data);
      setLeads(leadList.data.leads);
      setFunnel(funnelRes.data);
      sessionStorage.setItem("od-admin-key", k);
      setKey(k);
      setError("");
    } catch {
      setError("That key didn't work.");
      sessionStorage.removeItem("od-admin-key");
    }
  };

  if (!key || !data) {
    return (
      <main className="pt-16 sm:pt-20 bg-[#F8FAFC] min-h-screen">
        <div className="max-w-sm mx-auto px-4 py-24">
          <div className="rounded-3xl bg-white border border-slate-200 p-8 text-center">
            <span className="w-11 h-11 rounded-full bg-brand-tealsoft text-brand-teal flex items-center justify-center mx-auto mb-5">
              <Lock size={18} />
            </span>
            <h1 className="font-display font-bold text-2xl text-brand-navy mb-2">OceanData admin</h1>
            <p className="text-sm text-slate-500 mb-6">Miami MVP dashboard. Admin key required.</p>
            <input
              data-testid="admin-key-input"
              type="password"
              placeholder="Admin key"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && input && load(input)}
              className="w-full rounded-xl border border-slate-200 px-4 py-3.5 text-sm text-brand-navy outline-none focus:border-brand-teal focus:ring-2 focus:ring-brand-teal/20 mb-4"
            />
            <button
              data-testid="admin-login-button"
              onClick={() => input && load(input)}
              className="w-full rounded-full bg-brand-navy text-white text-sm font-semibold px-6 py-3.5 transition-colors duration-300 hover:bg-brand-teal"
            >
              Open dashboard
            </button>
            {error && <p data-testid="admin-error" className="text-xs text-red-500 mt-3">{error}</p>}
          </div>
        </div>
      </main>
    );
  }

  const s = data;
  const topIndustry = Object.entries(s.industry_interest).filter(([k]) => k !== "—")[0];
  const topProcess = Object.entries(s.process_interest).filter(([k]) => k !== "—")[0];

  return (
    <main className="pt-16 sm:pt-20 bg-[#F8FAFC] min-h-screen">
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-14">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-2">Admin · Miami MVP</p>
            <h1 data-testid="admin-title" className="font-display font-bold text-3xl text-brand-navy">What's working in Miami</h1>
          </div>
          <button
            data-testid="admin-refresh"
            onClick={() => load(key)}
            className="rounded-full border border-slate-200 bg-white text-sm font-semibold text-brand-navy px-5 py-2.5 hover:border-brand-teal transition-colors"
          >
            Refresh
          </button>
        </div>

        {/* The four dashboard questions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <div data-testid="admin-stat-industry" className="rounded-2xl bg-white border border-slate-200 p-6">
            <BarChart3 size={17} className="text-brand-teal mb-3" />
            <p className="text-xs text-slate-400 mb-1">Most-interested industry</p>
            <p className="font-display font-bold text-xl text-brand-navy">{topIndustry ? topIndustry[0] : "—"}</p>
            <p className="text-xs text-slate-400 mt-1">{topIndustry ? `${topIndustry[1]} selections` : "no data yet"}</p>
          </div>
          <div data-testid="admin-stat-process" className="rounded-2xl bg-white border border-slate-200 p-6">
            <Timer size={17} className="text-brand-teal mb-3" />
            <p className="text-xs text-slate-400 mb-1">Most-engaged process</p>
            <p className="font-display font-bold text-xl text-brand-navy">{topProcess ? topProcess[0] : "—"}</p>
            <p className="text-xs text-slate-400 mt-1">{topProcess ? `${topProcess[1]} views` : "no data yet"}</p>
          </div>
          <div data-testid="admin-stat-capacity" className="rounded-2xl bg-white border border-slate-200 p-6">
            <MapPin size={17} className="text-brand-teal mb-3" />
            <p className="text-xs text-slate-400 mb-1">Avg weekly capacity reported</p>
            <p className="font-display font-bold text-xl text-brand-navy">{s.avg_weekly_hours} hrs</p>
            <p className="text-xs text-slate-400 mt-1">{s.calculators_completed} calculators completed</p>
          </div>
          <div data-testid="admin-stat-leads" className="rounded-2xl bg-brand-navy text-white p-6">
            <Users size={17} className="text-brand-gold mb-3" />
            <p className="text-xs text-slate-400 mb-1">Qualified Miami leads</p>
            <p className="font-display font-bold text-xl">{s.leads}</p>
            <p className="text-xs text-slate-400 mt-1">{s.dataset_rows} targeting rows on file (private)</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-10">
          {/* Event funnel */}
          <div className="rounded-2xl bg-white border border-slate-200 p-7">
            <p className="font-mono text-xs uppercase tracking-[0.16em] text-brand-teal font-semibold mb-5">Event funnel</p>
            <div className="space-y-3">
              {Object.entries(EVENT_LABELS).map(([k, label]) => {
                const n = s.event_counts[k] || 0;
                const max = Math.max(...Object.values(s.event_counts), 1);
                return (
                  <div key={k} data-testid={`admin-event-${k}`} className="flex items-center gap-3">
                    <span className="w-40 text-xs text-slate-500 shrink-0">{label}</span>
                    <div className="flex-1 h-5 rounded-md bg-slate-100 overflow-hidden">
                      <div className="h-full bg-brand-teal/80 rounded-md transition-all duration-500" style={{ width: `${(n / max) * 100}%` }} />
                    </div>
                    <span className="w-8 text-right font-mono text-xs text-brand-navy font-semibold">{n}</span>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-slate-400 mt-5">
              Geo split: {Object.entries(s.geo_split).map(([k, v]) => `${k}: ${v}`).join(" · ") || "no data"}
            </p>
          </div>

          {/* Leads */}
          <div className="rounded-2xl bg-white border border-slate-200 p-7">
            <p className="font-mono text-xs uppercase tracking-[0.16em] text-brand-teal font-semibold mb-5">Miami leads</p>
            {leads && leads.length > 0 ? (
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {leads.map((l, i) => (
                  <div key={i} data-testid={`admin-lead-${i}`} className="rounded-xl bg-[#F8FAFC] border border-slate-100 p-4 [overflow-wrap:anywhere]">
                    <p className="font-display font-semibold text-sm text-brand-navy">{l.name} {l.company ? `· ${l.company}` : ""}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{l.email}{l.phone ? ` · ${l.phone}` : ""}</p>
                    <p className="text-xs text-slate-400 mt-1.5">
                      {l.industry} · {l.bottleneck || l.time_drain}
                      {l.weekly_hours ? ` · ~${l.weekly_hours} hrs/wk` : ""}
                      {l.est_annual_value ? ` · ≈$${Math.round(l.est_annual_value).toLocaleString()}/yr` : ""}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-400">No Miami leads yet.</p>
            )}
          </div>
        </div>

        {/* Simplification modal funnel — per locale */}
        <div data-testid="admin-funnel" className="rounded-2xl bg-white border border-slate-200 p-7 mb-10">
          <p className="font-mono text-xs uppercase tracking-[0.16em] text-brand-teal font-semibold mb-5">
            Simplification modal funnel — by locale
          </p>
          {funnel && funnel.events_total > 0 ? (
            <>
              <div>
                <table className="w-full table-fixed text-xs sm:text-sm [overflow-wrap:anywhere]">
                  <thead>
                    <tr className="text-left text-xs text-slate-400 border-b border-slate-100">
                      <th className="pb-3 font-medium">Locale</th>
                      <th className="pb-3 font-medium">Opened</th>
                      <th className="pb-3 font-medium">Reached step 2</th>
                      <th className="pb-3 font-medium">Submitted</th>
                      <th className="pb-3 font-medium">Open → step 2</th>
                      <th className="pb-3 font-medium">Open → submit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {["en", "fr", "es"].filter((l) => funnel.by_locale[l]).map((l) => {
                      const f = funnel.by_locale[l];
                      return (
                        <tr key={l} data-testid={`admin-funnel-${l}`} className="border-b border-slate-50 text-brand-navy">
                          <td className="py-3 font-mono text-xs font-semibold uppercase">{l}</td>
                          <td className="py-3">{f.modal_opened || 0}</td>
                          <td className="py-3">{f.step1_completed || 0}</td>
                          <td className="py-3 font-semibold">{f.modal_submitted || 0}</td>
                          <td className="py-3">{f.step2_rate}%</td>
                          <td className="py-3 font-semibold text-brand-teal">{f.submit_rate}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-slate-400 mt-5 [overflow-wrap:anywhere]">
                Opens by page: {Object.entries(funnel.opens_by_source).map(([k, v]) => `${k}: ${v}`).join(" · ") || "no data"}
              </p>
            </>
          ) : (
            <p className="text-sm text-slate-400">
              No modal events yet — they'll appear here as visitors use the "Show me where I can simplify" modal in each language.
            </p>
          )}
        </div>

        {/* Follow-up preference — voice call vs written reply, per language */}
        <div data-testid="admin-followup" className="rounded-2xl bg-white border border-slate-200 p-7 mb-10">
          <p className="font-mono text-xs uppercase tracking-[0.16em] text-brand-teal font-semibold mb-5">
            Follow-up preference — voice vs written, by locale · All time
          </p>
          {funnel && funnel.followup_by_locale && Object.keys(funnel.followup_by_locale).length > 0 ? (
            <div>
              <table className="w-full table-fixed text-xs sm:text-sm [overflow-wrap:anywhere]">
                <thead>
                  <tr className="text-left text-xs text-slate-400 border-b border-slate-100">
                    <th className="pb-3 font-medium">Locale</th>
                    <th className="pb-3 font-medium">Requests</th>
                    <th className="pb-3 font-medium">Voice call</th>
                    <th className="pb-3 font-medium">Written reply</th>
                    <th className="pb-3 font-medium">Voice share</th>
                    <th className="pb-3 font-medium">Written share</th>
                  </tr>
                </thead>
                <tbody>
                  {["en", "fr", "es"].filter((l) => funnel.followup_by_locale[l]).map((l) => {
                    const p = funnel.followup_by_locale[l];
                    return (
                      <tr key={l} data-testid={`admin-followup-${l}`} className="border-b border-slate-50 text-brand-navy">
                        <td className="py-3 font-mono text-xs font-semibold uppercase">{l}</td>
                        <td className="py-3">{p.total}</td>
                        <td className="py-3 font-semibold">{p.voice}</td>
                        <td className="py-3">{p.email}</td>
                        <td data-testid={`admin-voice-share-${l}`} className="py-3 font-semibold text-brand-teal">{p.total ? `${p.voice_rate}%` : "—"}</td>
                        <td data-testid={`admin-written-share-${l}`} className="py-3">{p.total ? `${p.email_rate}%` : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-slate-400">
              No requests yet — voice vs written preference appears here once visitors submit the modal in any language.
            </p>
          )}
        </div>


        <p className="text-xs text-slate-400">
          The 350-row targeting dataset (contractors, plumbing, commercial property) is stored privately in the backend and never exposed publicly.
        </p>
      </div>
    </main>
  );
}
